import { useEffect, useRef, useState } from "react";
import type { Landing } from "../../types/domain";
import { LANDING_STATUS_META } from "../landings/landingStatus";
import { mdPreview } from "../../lib/md";
import { IcoClose, IcoDoc, IcoPlus, IcoSearch } from "../../components/icons";

interface LandingPickerProps {
  landing: Landing | null;
  allLandings: Landing[];
  activeLaunchId: string;
  draftTitle: string;
  onLink: (landingId: string) => void;
  onUnlink: () => void;
  onCreateLanding: (titleSeed: string) => Promise<string>;
  onOpenEditor: (landingId: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

export function LandingPicker({
  landing,
  allLandings,
  activeLaunchId,
  draftTitle,
  onLink,
  onUnlink,
  onCreateLanding,
  onOpenEditor,
  disabled,
  readOnly = false,
}: LandingPickerProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [pickerOpen]);

  if (landing) {
    const meta = LANDING_STATUS_META[landing.status];
    const previewText = mdPreview(landing.content, 220);
    return (
      <div className="landing-linked-card">
        <div className="linked-card-head">
          <span
            className="landing-card-status"
            style={{ color: meta.color, background: meta.bg }}
          >
            <span className="status-dot" style={{ background: meta.dotColor }} />
            {meta.label}
          </span>
          <div className="linked-card-title">{landing.title}</div>
          <div className="linked-card-actions">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => onOpenEditor(landing.id)}
              disabled={disabled}
            >
              Открыть редактор →
            </button>
            {!readOnly && (
              <button
                type="button"
                className="launch-card-menu-btn"
                title="Отвязать"
                onClick={onUnlink}
                disabled={disabled}
              >
                <IcoClose size={13} />
              </button>
            )}
          </div>
        </div>
        <div className="linked-card-preview">
          {previewText || (
            <span style={{ color: "var(--text-muted)" }}>Лендинг пустой</span>
          )}
        </div>
        {landing.url && (
          <div className="linked-card-url">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <a
              href={landing.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {landing.url}
            </a>
          </div>
        )}
      </div>
    );
  }

  const filtered = allLandings
    .filter((l) => {
      if (!query.trim()) return true;
      const hay = (l.title + " " + (l.content ?? "")).toLowerCase();
      return hay.includes(query.trim().toLowerCase());
    })
    .slice()
    .sort((a, b) => {
      const ao = a.launchId === activeLaunchId ? 0 : 1;
      const bo = b.launchId === activeLaunchId ? 0 : 1;
      return ao - bo;
    });

  const handleCreate = async () => {
    if (creating || disabled) return;
    setCreating(true);
    try {
      const id = await onCreateLanding(draftTitle);
      onOpenEditor(id);
    } finally {
      setCreating(false);
    }
  };

  if (readOnly) {
    return (
      <div className="landing-empty-picker">
        <div className="landing-empty-msg" style={{ color: "var(--text-muted)" }}>
          Лендинг не привязан.
        </div>
      </div>
    );
  }

  return (
    <div className="landing-empty-picker">
      <div className="landing-empty-msg">
        Этот тип карточки — лендинг. Привяжите существующий из библиотеки или создайте
        новый.
      </div>
      <div className="landing-empty-actions">
        <button
          type="button"
          className="btn-primary btn-sm"
          onClick={handleCreate}
          disabled={creating || disabled}
        >
          <IcoPlus size={13} stroke="white" sw={2.5} />
          {creating ? "Создаём…" : "Создать и открыть редактор"}
        </button>
        <div className="landing-pick-wrap" ref={wrapRef}>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setPickerOpen((v) => !v)}
            disabled={disabled}
          >
            <IcoDoc size={13} /> Привязать существующий
          </button>
          {pickerOpen && (
            <div className="landing-pick-dropdown">
              <div className="landing-pick-search">
                <IcoSearch size={12} stroke="#7A849B" />
                <input
                  placeholder="Поиск по лендингам…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="landing-pick-list">
                {filtered.length === 0 ? (
                  <div className="landing-pick-empty">Ничего не найдено</div>
                ) : (
                  filtered.map((l) => (
                    <button
                      type="button"
                      key={l.id}
                      className="landing-pick-item"
                      onClick={() => {
                        onLink(l.id);
                        setPickerOpen(false);
                      }}
                    >
                      <div className="landing-pick-item-title">{l.title}</div>
                      <div className="landing-pick-item-meta">
                        {l.launchId === activeLaunchId && (
                          <span className="landing-pick-chip">этот запуск</span>
                        )}
                        <span>{mdPreview(l.content, 60)}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
